'use client'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useTrading } from '@/providers/TradingProvider'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

const AUTO_RETRY_DELAY_MS = 5000
const MAX_AUTO_RETRIES = 3

export function TradingSessionIndicator() {
	const { status, currentAction, error, retry, eoaAddress } = useTrading()
	const t = useTranslations('trading')
	const tCommon = useTranslations('common')

	// Auto-retry on error
	const autoRetryCountRef = useRef(0)

	useEffect(() => {
		if (status === 'error') {
			if (autoRetryCountRef.current >= MAX_AUTO_RETRIES) return

			const timer = setTimeout(() => {
				autoRetryCountRef.current += 1
				retry()
			}, AUTO_RETRY_DELAY_MS)

			return () => clearTimeout(timer)
		}

		// Reset counter on success
		if (status === 'ready') {
			autoRetryCountRef.current = 0
		}
	}, [status, retry])

	// Don't show if not logged in
	if (!eoaAddress) {
		return null
	}

	// Don't show if disconnected (will auto-init)
	if (status === 'disconnected') {
		return null
	}

	const statusConfig = {
		initializing: {
			color: 'text-yellow-400',
			icon: <Loader2 className="h-3 w-3 animate-spin" />,
			label: t('connecting'),
			badge: t('initializing'),
		},
		ready: {
			color: 'text-green-400',
			icon: <CheckCircle2 className="h-3 w-3" />,
			label: t('ready'),
			badge: t('active'),
		},
		error: {
			color: 'text-red-400',
			icon: <AlertCircle className="h-3 w-3" />,
			label: tCommon('error'),
			badge: t('failed'),
		},
	}[status]

	if (!statusConfig) {
		return null
	}

	const retriesExhausted = autoRetryCountRef.current >= MAX_AUTO_RETRIES

	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					type="button"
					className={cn(
						'flex items-center gap-1.5 text-xs transition-colors hover:text-foreground',
						statusConfig.color
					)}
				>
					{statusConfig.icon}
					<span className="hidden sm:inline">{statusConfig.label}</span>
				</button>
			</PopoverTrigger>
			<PopoverContent className="!z-999999999999 w-72" align="start" side="top">
				<div className="space-y-3">
					{/* Header */}
					<div className="flex items-center justify-between">
						<h4 className="font-medium text-sm">{t('polymarketTrading')}</h4>
						<span className={cn('flex items-center gap-1 text-xs', statusConfig.color)}>
							{statusConfig.icon}
							{statusConfig.badge}
						</span>
					</div>

					{/* Initializing state */}
					{status === 'initializing' && (
						<div className="space-y-2">
							<div className="text-muted-foreground text-xs">
								{currentAction || t('connecting')}
							</div>
							<div className="h-1 overflow-hidden rounded-full bg-muted">
								<div className="h-full w-2/3 animate-pulse rounded-full bg-yellow-400" />
							</div>
						</div>
					)}

					{/* Ready state */}
					{status === 'ready' && (
						<p className="text-muted-foreground text-xs">{t('youCanPlaceOrders')}</p>
					)}

					{/* Error state */}
					{status === 'error' && (
						<div className="space-y-2">
							<div className="rounded border border-red-500/20 bg-red-500/10 p-2">
								<p className="text-red-400 text-xs">{error?.message || t('connectionFailed')}</p>
							</div>
							{retriesExhausted ? (
								<Button
									size="sm"
									variant="outline"
									className="h-7 w-full text-xs"
									onClick={() => {
										autoRetryCountRef.current = 0
										retry()
									}}
								>
									{tCommon('retry')}
								</Button>
							) : (
								<p className="text-muted-foreground text-xs">Retrying automatically...</p>
							)}
						</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	)
}
